import { isEmpty, isNotDefined } from '@sgme/fp';
import { Dropdown } from '@sgme/ui';
import { useSgwtWidgets } from '@sgwt/sgwt-widgets-react';
import { useCallback, useRef } from 'react';
import { useIntl } from 'react-intl';
import { v4 } from 'uuid';

import { getMaxDate } from '@/api/dates/utils.ts';
import { selectGetCurrentUser } from '@/api/user/index.ts';
import { BACKGROUND_LIGHT_COLOR } from '@/App/tiles/CashTile/constants.ts';
import {
  useCheckForwardAccumulatorByTradeCapture,
  useForwardAccumulatorSelector,
  useForwardAccumulatorTileId,
} from '@/App/tiles/ForwardAccumulator/ProductProvider.tsx';
import { PredealChecksIndicator } from '@/App/tiles/shared/PredealChecksIndicator.tsx';
import ToggleProduct from '@/App/tiles/ToggleProduct.tsx';
import { ChangeClientDropDownItem } from '@/components/ClientSelector/ChangeClientDropDownItem.tsx';
import { TileOverridenClientIndicator } from '@/components/ClientSelector/TileOverridenClientIndicator.tsx';
import { isDefined } from '@/components/sg-design-system/utils/index.ts';
import CurrencyPairPicker from '@/components/ui/CurrencyPairPicker/index.tsx';
import { forwardAccumulatorSlice } from '@/store/forwardAccumulator/slice.ts';
import {
  copyToClipBoardForwardAccumulatorThunk,
  resetForwardAccumulatorThunk,
} from '@/store/forwardAccumulator/thunk.ts';
import { useAppDispatch } from '@/store/hooks.ts';
import { selectTileClientId } from '@/store/tiles/selectors.ts';
import { duplicateTile, removeTile } from '@/store/tiles/slice.ts';

export const Header = () => {
  const tileId = useForwardAccumulatorTileId();
  const check = useCheckForwardAccumulatorByTradeCapture();

  const { formatNumber } = useIntl();
  const { sgwtWebAnalytics } = useSgwtWidgets();

  const dispatch = useAppDispatch();

  const dropdownToggleRef = useRef<HTMLButtonElement>(null);

  const { currencyPair, clientId, status, currentUserCache, TileClientId, globalPreDealCheck, predealChecks } =
    useForwardAccumulatorSelector((forwardAccumulator, state) => {
      const clientId = selectTileClientId(state, tileId);
      const currentUserCache = selectGetCurrentUser(state).data?.clients;

      const predealChecks =
        forwardAccumulator.properties.rfs?.status === 'PRICING'
          ? forwardAccumulator.properties.rfs.quote.predealChecks
          : undefined;
      const globalPreDealCheck = isNotDefined(predealChecks)
        ? 'Unknown'
        : predealChecks.filter((check) => check.type === 'Icon' && check.statusHealth === 'Nok').length > 0
          ? 'Nok'
          : 'Ok';

      return {
        currencyPair: forwardAccumulator.properties.currencyPair,
        globalPreDealCheck,
        predealChecks,
        status: forwardAccumulator.tradeCapture.status,
        TileClientId: forwardAccumulator.clientId,
        currentUserCache,
        clientId,
      };
    });

  const handleCurrencyPairChange = useCallback(
    async (value: string) => {
      if (isDefined(currentUserCache) && isDefined(clientId)) {
        const maxDate = await getMaxDate(clientId, currentUserCache, dispatch, value);
        dispatch(forwardAccumulatorSlice.actions.setForwardAccumulatorMaxDate({ tileId, maxDate }));
      }
      sgwtWebAnalytics?.trackEvent('ForwardAccumulator', 'Currency pair changed', value);

      if (status !== 'INITIALIZED') {
        dispatch(resetForwardAccumulatorThunk({ tileId, forcedCurrencyPair: value }));
      } else {
        check({
          currencyPair: value,
        });
      }
    },
    [check, sgwtWebAnalytics, currentUserCache, status, clientId, tileId],
  );

  const handleDuplicate = useCallback(() => {
    sgwtWebAnalytics?.trackEvent('ForwardAccumulator', 'Tile duplicated', tileId);
    dispatch(duplicateTile({ sourceTileId: tileId, newTileId: v4() }));
  }, [sgwtWebAnalytics, tileId]);

  const handleReset = useCallback(() => {
    sgwtWebAnalytics?.trackEvent('ForwardAccumulator', 'Tile reseted', tileId);
    dispatch(resetForwardAccumulatorThunk({ tileId }));
  }, [sgwtWebAnalytics, tileId]);

  const handleClose = useCallback(() => {
    sgwtWebAnalytics?.trackEvent('ForwardAccumulator', 'Tile closed', tileId);
    dispatch(removeTile(tileId));
  }, [sgwtWebAnalytics, tileId]);

  const handleCopyToClipboard = useCallback(() => {
    sgwtWebAnalytics?.trackEvent('ForwardAccumulator', 'Tile copied to clipboard', tileId);
    dispatch(copyToClipBoardForwardAccumulatorThunk({ tileId, formatNumber }));
  }, [sgwtWebAnalytics, tileId, formatNumber]);

  const handleSelectClient = useCallback(
    (clientId: number) => {
      sgwtWebAnalytics?.trackEvent('ForwardAccumulator', 'Tile client selected', String(clientId));
      dispatch(
        forwardAccumulatorSlice.actions.forwardAccumulatorClientIdUpdated({
          tileId,
          clientId,
          isTabClientOverriden: true,
        }),
      );
    },
    [sgwtWebAnalytics, tileId],
  );

  const handleClearSelectedClient = useCallback(() => {
    sgwtWebAnalytics?.trackEvent('ForwardAccumulator', 'Tile client reseted', tileId);
    dispatch(
      forwardAccumulatorSlice.actions.forwardAccumulatorClientIdUpdated({
        tileId,
        clientId: undefined,
        isTabClientOverriden: true,
      }),
    );
  }, [sgwtWebAnalytics, tileId]);

  return (
    <>
      <TileOverridenClientIndicator clientId={TileClientId} onRemoveOverride={handleClearSelectedClient} />
      <div style={{ display: 'grid', gridTemplateColumns: '1fr auto 1fr', alignItems: 'center' }} className="pb-8px">
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <ToggleProduct tileId={tileId} />
          <PredealChecksIndicator predealChecks={predealChecks} globalPreDealCheck={globalPreDealCheck ?? 'Unknown'} />
        </div>

        <CurrencyPairPicker
          tradeType="Accumulator"
          value={currencyPair}
          onChange={handleCurrencyPairChange}
          data-e2e="currency-pair-input"
        />

        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
          <Dropdown color={BACKGROUND_LIGHT_COLOR} direction="auto" size="md" data-e2e="tile-menu">
            <Dropdown.Toggle className="btn-icon" data-e2e="tileMenu-btn">
              <i className="icon" ref={dropdownToggleRef}>
                more_vert
              </i>
            </Dropdown.Toggle>

            <Dropdown.Menu>
              <ChangeClientDropDownItem triggerRef={dropdownToggleRef} onSelectClient={handleSelectClient} />
              <Dropdown.Item
                onClick={handleDuplicate}
                state={isEmpty(currencyPair) ? 'disabled' : undefined}
                data-e2e="duplicate-tile-menu"
              >
                Duplicate
              </Dropdown.Item>
              <Dropdown.Item
                onClick={handleReset}
                state={isEmpty(currencyPair) ? 'disabled' : undefined}
                data-e2e="reset-tile-menu"
              >
                Reset
              </Dropdown.Item>
              <Dropdown.Item
                onClick={handleCopyToClipboard}
                state={isEmpty(currencyPair) ? 'disabled' : undefined}
                data-e2e="copyToClipboard-tile-menu"
              >
                Copy to clipboard
              </Dropdown.Item>
              <Dropdown.Item onClick={handleClose} data-e2e="close-tile-menu">
                Close
              </Dropdown.Item>
            </Dropdown.Menu>
          </Dropdown>
        </div>
      </div>
    </>
  );
};
